import {PERMISSION_TYPE} from '../../../../Permissions';
import {hasPermission, hasTaskUpdatePermission, isClientUser} from '../../util/PermissionsUtil';
import {deriveControlState} from './controlState';
import {deriveEligibilityFromTask} from './eligibility';

// The two person-level facts every control is given, read without a viewer in hand. The client
// restriction is Util.isClientTaskActionsRestricted's own expression; util.js itself is not imported
// because its import chain reaches the canvas and chart modules, which every table that asks this
// would then load.
const personLevelEligibilityInput = () => ({
	canEditTask: hasTaskUpdatePermission(),
	isClientActionsRestricted: isClientUser() && hasPermission(PERMISSION_TYPE.CLIENT_RESTRICTED_TASK_ACTIONS),
});

/**
 * Whether this person may act on tasks at all, as a property of them rather than of any one task.
 *
 * The same two facts every mounted control already asks about itself — the edit-tasks permission,
 * and a client user restricted from acting on tasks — read here without a task in hand, because a
 * table has to decide about its whole column before it knows what is in any row.
 *
 * A task's own lock is deliberately not part of it. That is per row, and a table of locked tasks
 * belonging to somebody who can normally act is a different thing from a person who can never act.
 */
export function viewerCanActOnTasks(): boolean {
	// The control's own eligibility, asked of no task in particular - so the column and the control
	// cannot come to different answers about the same person.
	return deriveEligibilityFromTask({readOnly: null, ...personLevelEligibilityInput()}) === null;
}

// Every shape the task tables hand their rows around in: a Relay edge, a bare task, and the
// nested tree the scoping table builds - whose rows carry the task under `task`, beside the phase
// and grouping rows around them. Walked rather than required to be flat, because a parent task
// with no conversation can have a subtask that has one, and the column has to count it.
interface TaskLikeRow {
	hasNovaConversation?: boolean | null;
	node?: TaskLikeRow | null;
	task?: TaskLikeRow | null;
	children?: ReadonlyArray<TaskLikeRow> | null;
	edges?: ReadonlyArray<TaskLikeRow> | null;
}

export function anyTaskHasNovaConversation(rows: ReadonlyArray<TaskLikeRow | null | undefined> | null | undefined): boolean {
	if (!rows) return false;
	return rows.some(row => {
		if (!row) return false;
		if (row.hasNovaConversation) return true;
		if (row.node?.hasNovaConversation) return true;
		if (row.task?.hasNovaConversation) return true;
		return (
			anyTaskHasNovaConversation(row.children) ||
			anyTaskHasNovaConversation(row.edges) ||
			anyTaskHasNovaConversation(row.node ? [row.node] : null)
		);
	});
}

/**
 * Whether a task table draws its Nova column at all, header included.
 *
 * Somebody who can act gets it always: every row offers them a run, whether or not that task has
 * been run before. Somebody who cannot gets a control only where there is an answer to read, so a
 * table with no answers in it would be a column of empty cells under a heading for a feature they
 * cannot use — and the column goes instead. One answer anywhere in the table brings it back, and
 * the rows without one get the empty cell any other column would give them.
 *
 * This is separate from the eye menu. Whoever may hide a column today may still hide this one;
 * that is a preference, and this is whether there is anything for the preference to be about.
 */
export function shouldShowNovaColumn(rows: ReadonlyArray<TaskLikeRow | null | undefined> | null | undefined): boolean {
	return viewerCanActOnTasks() || anyTaskHasNovaConversation(rows);
}

/**
 * Whether this one task's Nova control would draw anything at all.
 *
 * The card surfaces have no column to remove, so they ask per card: a slot that renders nothing
 * still holds its width, and the card header is meant to close up instead. Asked of the control's
 * own decision rather than restated, so the two cannot drift: a standing block leaves View where
 * there is an answer to read, and nothing where there is not. Session run state is not available
 * to a card that has not mounted its control, so only what the server says is read here.
 */
export function novaControlWouldRender(task: {
	hasNovaConversation?: boolean | null;
	readOnly?: {isReadOnly: boolean} | null;
}): boolean {
	const eligibility = deriveEligibilityFromTask({readOnly: task.readOnly ?? null, ...personLevelEligibilityInput()});
	const state = deriveControlState({
		hasEverRun: false,
		isRunning: false,
		outputRead: false,
		eligibility,
		hasConversation: !!task.hasNovaConversation,
	});
	return state.kind !== 'hidden';
}
